// Cliente HTTP pro reviter-api. Usa fetch nativo.
//
// Em dev: package.json aponta proxy pra http://localhost:3020 → URLs relativas
// funcionam. Em produção: REACT_APP_API_URL aponta pro host real.
//
// Token de acesso é lido do localStorage ('reviterAuth' setado pelo AuthContext).
// X-Store-Slug é lido do localStorage ('reviterCurrentStoreSlug') quando presente
// — permite o frontend operar em rotas tenant-scoped sem depender do hostname
// durante dev.

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

function readAuth() {
  try {
    const raw = localStorage.getItem('reviterAuth');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getMembershipStoreId(membership) {
  if (!membership) return null;
  return membership.storeId ?? membership.id ?? membership.Store?.id ?? null;
}

function currentStoreSlug() {
  const explicit = localStorage.getItem('reviterCurrentStoreSlug');
  if (explicit) return explicit;

  // Fallback defensivo: em alguns fluxos a tela dispara requests antes do
  // AuthContext sincronizar o slug no localStorage. Derivamos do reviterAuth.
  const auth = readAuth();
  const currentStoreId = auth?.currentStoreId ?? null;
  const stores = auth?.stores || [];
  if (!currentStoreId || !stores.length) return null;

  const membership = stores.find((s) => getMembershipStoreId(s) === currentStoreId);
  return membership?.slug || null;
}

function buildHeaders(extra = {}, { includeAuth = true, includeStore = true } = {}) {
  const headers = { 'Content-Type': 'application/json', ...extra };
  if (includeAuth) {
    const auth = readAuth();
    if (auth?.token) headers.Authorization = `Bearer ${auth.token}`;
  }
  if (includeStore) {
    const slug = currentStoreSlug();
    if (slug) headers['X-Store-Slug'] = slug;
  }
  return headers;
}

async function parseResponse(res) {
  // 304 = Not Modified (cache condicional do ETag). Sem body.
  // Fetch considera 304 como !res.ok — tratamos como sucesso "sem dados
  // novos" (caller deve usar cache ou simplesmente aceitar null).
  if (res.status === 304) return null;
  const text = await res.text();
  const body = text ? safeJson(text) : null;
  if (!res.ok) {
    const error = new Error(body?.error || `HTTP ${res.status}`);
    error.status = res.status;
    error.details = body?.details;
    error.body = body;
    throw error;
  }
  return body;
}

function safeJson(text) {
  try { return JSON.parse(text); } catch { return text; }
}

// refresh lazy: se tiver refreshToken, tenta renovar o access token uma vez
let refreshInFlight = null;
async function tryRefresh() {
  if (refreshInFlight) return refreshInFlight;
  const auth = readAuth();
  if (!auth?.refreshToken) return null;

  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: auth.refreshToken })
      });
      if (!res.ok) return null;
      const data = await res.json();
      const updated = { ...auth, token: data.token, refreshToken: data.refreshToken };
      localStorage.setItem('reviterAuth', JSON.stringify(updated));
      return updated.token;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

async function request(path, { method = 'GET', body, headers, includeAuth = true, includeStore = true } = {}) {
  const url = `${API_BASE_URL}${path}`;
  const opts = {
    method,
    headers: buildHeaders(headers, { includeAuth, includeStore }),
    body: body != null ? JSON.stringify(body) : undefined
  };

  let res = await fetch(url, opts);

  // Auto-refresh em 401 (uma tentativa)
  if (res.status === 401 && includeAuth) {
    const newToken = await tryRefresh();
    if (newToken) {
      opts.headers = buildHeaders(headers, { includeAuth, includeStore });
      res = await fetch(url, opts);
    } else {
      // Refresh falhou: limpa auth e redireciona pro login com motivo.
      // O AuthContext reage via storage event. Whitelist evita loop se
      // a requisição que 401'u foi justamente o próprio /auth/login.
      localStorage.removeItem('reviterAuth');
      const here = window.location.pathname;
      const publicPaths = ['/entrar', '/cadastrar', '/esqueci-senha', '/redefinir-senha'];
      if (!publicPaths.some((p) => here.startsWith(p))) {
        window.location.assign('/entrar?reason=session-expired');
      }
    }
  }

  return parseResponse(res);
}

export const api = {
  get: (path, opts) => request(path, { ...opts, method: 'GET' }),
  post: (path, body, opts) => request(path, { ...opts, method: 'POST', body }),
  patch: (path, body, opts) => request(path, { ...opts, method: 'PATCH', body }),
  put: (path, body, opts) => request(path, { ...opts, method: 'PUT', body }),
  delete: (path, opts) => request(path, { ...opts, method: 'DELETE' })
};

export default api;
